import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import MultiChoice from "../../utils/MultiChoice";

// Expanded mode's fixed column layout — filters not listed here are appended
// as an extra trailing column so nothing silently disappears from the UI.
const EXPANDED_COLUMNS = [
  ["Players", "Winners", "Sets", "Games"],
  ["Shots", "Serves"],
  ["Forehand", "Outcome"],
  ["Backhand", "Reaches"],
  ["Scores", "Directions", "Rally Length"],
  ["Serve Form.", "Return Form.", "Net Position"],
];

// Only meaningful for doubles (serve/return formation + net position of the
// non-hitting partner) — hidden entirely for singles matches.
const DOUBLES_ONLY_FILTERS = new Set(["Serve Form.", "Return Form.", "Net Position"]);

function AllFiltersPanel({
  allOptions,
  matches,
  playerGuid,
  handleFilterOptionChanged,
  expanded = false,
  itemColumns = 1,
}) {
  const [, forceUpdate] = useState(0);
  const expandedScrollRef = useRef(null);

  // PC mice typically only have a vertical wheel; translate that into
  // horizontal scrolling here since a trackpad's native horizontal swipe
  // (which already works) isn't available on a mouse. Needs a real
  // (non-passive) listener since React's onWheel can't preventDefault.
  useEffect(() => {
    if (!expanded) return;
    const el = expandedScrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [expanded]);

  const setPlayerNames = () => {
    if (matches.length === 0) {
      return;
    }
    for (var n = 0; n < allOptions.length; n++) {
      var option = allOptions[n];
      var match = matches[0];
      if (option.title === "Players" || option.title === "Winners") {
        if (match.isDoubles) {
          const isTeamA =
            !playerGuid ||
            playerGuid === match.teamA?.guid ||
            playerGuid === match.teamA?.player1?.guid ||
            playerGuid === match.teamA?.player2?.guid;
          const focusTeam = isTeamA ? match.teamA : match.teamB;
          const oppoTeam = isTeamA ? match.teamB : match.teamA;
          // Both "Players" and "Winners" default to every player selected —
          // matches singles' default of both players shown for a single
          // match (singles never hides the opposition by default either;
          // doubles previously only pre-selected the focus team here, which
          // meant a whole service game hit by the "opposition" team could
          // default to showing nothing at all).
          const oppoDefaultSelected = true;
          // Default (when this slot hasn't already gone through a doubles
          // transform before): focus-team players selected, opposition not.
          // `existing.selected` is only trusted once `existing` itself is a
          // previously-produced doubles item (marked by having a `guid`) —
          // otherwise `existing` is really just the unrelated singles
          // Player1/Player2 default, whose Player-2-unselected `false` would
          // otherwise leak in as "the user deselected A2" on the very first
          // doubles transform (`false ?? default` still resolves to `false`).
          const base = (existing, defaultSelected) => ({
            selected: existing?.guid !== undefined ? existing.selected : defaultSelected,
            amount: existing?.amount ?? 0,
          });
          if (matches.length > 1) {
            option.items.splice(
              0,
              option.items.length,
              { ...base(option.items[0], true), name: focusTeam?.player1?.lastName ?? "A1", guid: focusTeam?.player1?.guid },
              { ...base(option.items[1], true), name: focusTeam?.player2?.lastName ?? "A2", guid: focusTeam?.player2?.guid }
            );
          } else {
            option.items.splice(
              0,
              option.items.length,
              { ...base(option.items[0], true), name: focusTeam?.player1?.lastName ?? "A1", guid: focusTeam?.player1?.guid },
              { ...base(option.items[1], true), name: focusTeam?.player2?.lastName ?? "A2", guid: focusTeam?.player2?.guid },
              { ...base(option.items[2], oppoDefaultSelected), name: oppoTeam?.player1?.lastName ?? "B1", guid: oppoTeam?.player1?.guid },
              { ...base(option.items[3], oppoDefaultSelected), name: oppoTeam?.player2?.lastName ?? "B2", guid: oppoTeam?.player2?.guid }
            );
          }
        } else {
          option.items.splice(2);
          delete option.items[0].guid;
          delete option.items[1].guid;
          option.items[0].name =
            match.player1Guid === playerGuid
              ? match.player1LastName
              : match.player2LastName;
          if (matches.length > 1) {
            option.items[1].name = "Oppositions";
          } else {
            option.items[1].name =
              match.player1Guid === playerGuid
                ? match.player2LastName
                : match.player1LastName;
          }
        }
      }
    }
  };

  const doOptionChanged = (options, item) => {
    if (options.title === "Scores") {
      var playersSelected = 0;
      for (var n = 0; n < allOptions.length; n++) {
        if (allOptions[n].title === "Players") {
          for (var i = 0; i < allOptions[n].items.length; i++) {
            playersSelected += allOptions[n].items[i].selected ? 1 : 0;
          }
          break;
        }
      }
      if (playersSelected !== 1) {
        toast.error(
          "Scores can not be analysed when all players are selected."
        );
        return;
      }
    }
    // forceUpdate(n => !n)
    handleFilterOptionChanged(allOptions);
  };

  const selectedOptions = (filter) => {
    var count = 0;
    var s = "";
    for (var n = 0; n < filter.items.length; n++) {
      if (filter.items[n].selected) {
        if (s.length > 0) s += ", ";
        s += (filter.items[n].name ?? "").toUpperCase();
        count++;
      }
    }
    if (count === filter.items.length) {
      return "ALL";
    } else if (count === 0) {
      return "NONE";
    }
    return s;
  };

  // Select-all/none for a filter's title row (moved out of MultiChoice so
  // they can sit on the same row as the title, far right).
  const selectAllForFilter = (filter) => {
    filter.allselected = true;
    for (var i = 0; i < filter.items.length; i++) {
      filter.items[i].selected = true;
    }
    doOptionChanged(filter, null);
  };

  const selectNoneForFilter = (filter) => {
    filter.allselected = false;
    for (var i = 0; i < filter.items.length; i++) {
      filter.items[i].selected = false;
    }
    doOptionChanged(filter, null);
  };

  setPlayerNames();

  // Serve/return formation and net position only apply to doubles.
  const isDoublesMatch = matches[0]?.isDoubles ?? false;
  const visibleOptions = isDoublesMatch
    ? allOptions
    : allOptions.filter((f) => !DOUBLES_ONLY_FILTERS.has(f.title));

  // Shared title-row content (title + Select All/None) for both the
  // collapsible (normal) and always-open (expanded) layouts.
  const filterTitleRow = (filter) => (
    <div className="pt-1 pb-.5 ml-1 bg-base-100 text-sm font-bold flex items-center justify-between">
      <div>{filter.title.toUpperCase()}</div>
      {/* relative z-10: sit above the collapse's overlay checkbox so
          these buttons receive the click instead of toggling it. */}
      <div
        className="relative z-10 flex gap-1 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn btn-xs btn-square btn-ghost rounded-none"
          title="Select All"
          onClick={() => selectAllForFilter(filter)}
        >
          +
        </button>
        <button
          type="button"
          className="btn btn-xs btn-square btn-ghost rounded-none"
          title="Select None"
          onClick={() => selectNoneForFilter(filter)}
        >
          -
        </button>
      </div>
    </div>
  );

  if (expanded) {
    // Expanded mode: every filter shown open (no collapse/arrow), grouped
    // into fixed columns (EXPANDED_COLUMNS) that each stack vertically.
    const byTitle = new Map(visibleOptions.map((f) => [f.title, f]));
    const placed = new Set();
    const columns = EXPANDED_COLUMNS.map((titles) =>
      titles.map((t) => byTitle.get(t)).filter(Boolean)
    ).filter((col) => col.length > 0);
    columns.forEach((col) => col.forEach((f) => placed.add(f.title)));
    const leftover = visibleOptions.filter((f) => !placed.has(f.title));
    if (leftover.length > 0) columns.push(leftover);

    return (
      <div
        ref={expandedScrollRef}
        className="w-full flex gap-2 items-start overflow-x-auto"
      >
        {columns.map((col, ci) => (
          <div key={ci} className="flex flex-col gap-0.5 w-[170px] shrink-0">
            {col.map((filter) => (
              <div
                key={filter.title}
                className="border border-base-300 rounded-none"
              >
                {filterTitleRow(filter)}
                <div className="bg-base-200">
                  <MultiChoice
                    filter={filter}
                    columns={filter.columns}
                    handleOptionChanged={(filter, item) =>
                      doOptionChanged(filter, item)
                    }
                  ></MultiChoice>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="w-[320px]">
        {visibleOptions.map((filter) => (
          <div
            key={filter.title}
            tabIndex={0}
            className="collapse collapse-arrow rounded-none border border-base-300"
          >
            <input type="checkbox" className="peer" />
            <div className="collapse-title pt-1 pb-.5 bg-base-100 text-sm font-bold flex items-center justify-between peer-checked:bg-base-200 peer-checked:text-base-content">
              <div>
                {filter.title.toUpperCase()}
                {/* <p className="text-xs font-normal">{selectedOptions(filter)}</p> */}
              </div>
              {/* relative z-10: sit above the collapse's overlay checkbox so
                  these buttons receive the click instead of toggling it. */}
              <div
                className="relative z-10 flex gap-1 shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="btn btn-xs btn-square rounded-none"
                  title="Select All"
                  onClick={() => selectAllForFilter(filter)}
                >
                  +
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-square rounded-none"
                  title="Select None"
                  onClick={() => selectNoneForFilter(filter)}
                >
                  -
                </button>
              </div>
            </div>
            <div className="collapse-content peer-checked:bg-base-200 ">
              <MultiChoice
                filter={filter}
                columns={filter.columns}
                handleOptionChanged={(filter, item) =>
                  doOptionChanged(filter, item)
                }
              ></MultiChoice>
            </div>
          </div>
        ))}
        <div className="collapse-title"></div>
        <div className="collapse-content"></div>
      </div>
    </>
  );
}

export default AllFiltersPanel;
